import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from '@/components/ui';
import type { PickupCompleteRequest } from '@/lib/api-admin/types';

export interface OrderPickupConfirmModalProps {
  orderNo: string;
  expectedPickupCode?: string | null;
  isOpen: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (body: PickupCompleteRequest) => void;
  onCancel: () => void;
}

export const OrderPickupConfirmModal = ({
  orderNo,
  expectedPickupCode,
  isOpen,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
}: OrderPickupConfirmModalProps) => {
  const { t } = useTranslation('orders');
  const [code, setCode] = useState('');
  const [actor, setActor] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setCode('');
      setActor('');
      setLocalError(null);
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError(null);

    const trimmedCode = code.trim();
    if (trimmedCode.length < 6) {
      setLocalError(t('orders.actionModal.pickupCodeRequired'));
      return;
    }
    if (
      typeof expectedPickupCode === 'string' &&
      expectedPickupCode.length > 0 &&
      trimmedCode !== expectedPickupCode
    ) {
      setLocalError(t('orders.actionModal.pickupCodeMismatch'));
      return;
    }
    const body: PickupCompleteRequest = { pickupCode: trimmedCode };
    if (actor.trim().length > 0) body.verifiedBy = actor.trim();
    onSubmit(body);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle>{t('orders.actions.pickup-confirm')}</DialogTitle>
        <DialogDescription>
          {t('orders.actionModal.subject', { orderNo })}
        </DialogDescription>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col" data-sentry-mask>
            <label
              htmlFor="order-pickup-code-input"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('orders.actionModal.pickupCodeConfirm')}
            </label>
            <Input
              id="order-pickup-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={12}
              inputMode="text"
              autoComplete="off"
              data-sentry-mask
            />
          </div>
          <div className="flex flex-col">
            <label
              htmlFor="order-pickup-verified-by"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('orders.actionModal.pickupVerifiedBy')}
            </label>
            <Input
              id="order-pickup-verified-by"
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              maxLength={100}
            />
          </div>

          {localError ? (
            <p className="text-sm" role="alert" style={{ color: 'var(--signal-danger)' }}>
              {localError}
            </p>
          ) : null}

          {errorMessage ? (
            <p className="text-sm" role="alert" style={{ color: 'var(--signal-danger)' }}>
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('orders.actions.cancel')}
            </Button>
            <Button type="submit" variant="cta" size="md" disabled={isSubmitting}>
              {isSubmitting ? t('orders.actionModal.submitting') : t('orders.actionModal.confirm')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
